import type { ApolloCache } from '@apollo/client';

/** The query fields whose cached results depend on what the probation review audit log holds. */
const AUDIT_LOG_FIELD = 'probationReviewAuditLogs';
const EMPLOYEE_LIST_FIELD = 'listEmployees';
/** The employee list that offers only the employees with an entry in the log carries this filter in its arguments. */
const WITH_ENTRIES_FILTER = 'hasProbationReviewAuditLog';

/**
 * The `employeeId` an audit log result was asked for, read from the cache field name, which holds the arguments of the
 * query as JSON (`probationReviewAuditLogs({"filter":{"employeeId":"7"},"pageNumber":1})`). `null` when the result was
 * not narrowed to one employee, `undefined` when the arguments cannot be read, so the caller drops the entry to be safe.
 */
const askedEmployeeId = (storeFieldName: string): string | null | undefined => {
  const start = storeFieldName.indexOf('(');
  if (start < 0 || !storeFieldName.endsWith(')')) {
    return undefined;
  }

  try {
    const args: unknown = JSON.parse(storeFieldName.slice(start + 1, -1));
    const filter = (args as { filter?: { employeeId?: unknown } | null } | null)?.filter;
    const employeeId = filter?.employeeId;

    if (employeeId === undefined || employeeId === null) {
      return null;
    }

    return typeof employeeId === 'string' ? employeeId : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Drops from the Apollo cache what a probation review change makes stale, so the next visit to the audit log reads the
 * server again instead of showing the entries from before the change.
 *
 * - The audit log results asked for the changed employees, and those asked for no single employee (they list the new
 *   entry too). Results narrowed to anyone else stay: the change does not touch them.
 * - The employee lists that keep only employees with an entry (the Employee filter of the audit log screen), since the
 *   first entry of an employee puts them on that list.
 *
 * Pass it as the `update` of a mutation: `update: cache => evictProbationReviewAuditLogs(cache, [employeeId])`.
 */
export const evictProbationReviewAuditLogs = (cache: ApolloCache<unknown>, employeeIds: readonly string[]): void => {
  const changed = new Set(employeeIds);

  cache.modify({
    fields: {
      [AUDIT_LOG_FIELD]: (existing, { storeFieldName, DELETE }) => {
        const asked = askedEmployeeId(storeFieldName);

        return asked === null || asked === undefined || changed.has(asked) ? DELETE : existing;
      },
      [EMPLOYEE_LIST_FIELD]: (existing, { storeFieldName, DELETE }) =>
        storeFieldName.includes(WITH_ENTRIES_FILTER) ? DELETE : existing,
    },
  });
};
